import React from "react";

import { FileCopyOutlined, ExpandMore } from "@material-ui/icons";
import {
  FormControl,
  FormLabel,
  Typography,
  TextInput,
  SelectInput,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@portex-pro/ui-components";
import { ContainerSize } from "api/types/generated-types";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import clsx from "clsx";
import { Guide } from "components/Guide";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getContainerSizeLabel } from "utils/fcl-containers/getContainerSizeLabel";

import { ContainerTypeTabPanel } from "./ContainerTypeTabPanel";

interface ContainerCardProps {
  index: number;
  expanded: boolean;
  onAccordionChange: (event: React.ChangeEvent<unknown>, isExpanded: boolean) => void;
  onRemove: () => void;
  onCopy: () => void;
  canRemove: boolean;
}

// Create container sizes based on ContainerSize enum, automatically translated
const containerSizes = Object.values(ContainerSize).map((size) => ({
  value: size,
  label: getContainerSizeLabel(size),
}));

export const ContainerCard: React.FC<ContainerCardProps> = ({
  index,
  expanded,
  onAccordionChange,
  onRemove,
  onCopy,
  canRemove,
}) => {
  const { t } = useTranslation("quoteRequest");
  const { control, watch, getFieldState } = useFormContext();
  const setQuoteRequestActionByPath = quoteRequestActions.useSetQuoteRequestByPath();

  const containerType = watch(`containers.${index}.type`);
  const containerSize = watch(`containers.${index}.size`);
  const containerCount = watch(`containers.${index}.count`);

  // Check if there are any errors in this container's fields
  const hasErrors =
    getFieldState(`containers.${index}.type`).error ||
    getFieldState(`containers.${index}.size`).error ||
    getFieldState(`containers.${index}.count`).error ||
    getFieldState(`containers.${index}.weight`).error ||
    getFieldState(`containers.${index}.volume`).error ||
    getFieldState(`containers.${index}.minTemp`).error ||
    getFieldState(`containers.${index}.maxTemp`).error;

  // Create summary for the accordion header
  const containerSizeLabel = containerSizes.find((size) => size.value === containerSize)?.label || "";
  const summaryTag =
    containerType && containerSizeLabel && containerCount ? (
      <span className="inline-flex items-center gap-1 bg-blue-50 rounded-lg px-3 py-1 ml-2">
        <span className="font-bold text-blue-600">
          {containerCount} x {containerType}
        </span>
        <span className="text-blue-600">{containerSizeLabel}</span>
      </span>
    ) : null;

  return (
    <Accordion
      expanded={expanded}
      onChange={onAccordionChange}
      className={clsx("border", hasErrors ? "border-red-500" : "border-gray-200", index === 0 ? "rounded-t-xl" : "")}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore
            className="transition-transform duration-200"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        }
      >
        <div className="flex items-center w-full justify-between">
          <span className="font-semibold text-base">Container {index + 1}</span>
          <div className="flex items-center">
            {summaryTag}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              className="text-gray-500 hover:text-blue-600 transition-colors ml-2"
              title="Copy container"
            >
              <FileCopyOutlined fontSize="small" />
            </button>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="space-y-4 w-full">
          <div>
            <FormControl fullWidth>
              <FormLabel required className="text-sm font-medium text-gray-700 mb-2">
                {t("ContainersStep.equipment")}
              </FormLabel>
              <Controller
                name={`containers.${index}.type`}
                control={control}
                render={({ field, fieldState }) => (
                  <Guide showGuide={!field.value && !fieldState.error}>
                    <ContainerTypeTabPanel
                      className="guideGroup"
                      selected={field.value}
                      onSelect={(type) =>
                        setQuoteRequestActionByPath({ path: `load_spec.containers.${index}.type`, value: type })
                      }
                      hasError={!!fieldState.error}
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </Guide>
                )}
              />
            </FormControl>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormControl>
              <FormLabel required className="text-sm font-medium text-gray-700 mb-2">
                {t("ContainersStep.containerSize")}
              </FormLabel>
              <Controller
                name={`containers.${index}.size`}
                control={control}
                render={({ field, fieldState }) => (
                  <Guide showGuide={!field.value && !fieldState.error}>
                    <SelectInput
                      {...field}
                      onChange={(e) =>
                        setQuoteRequestActionByPath({
                          path: `load_spec.containers.${index}.size`,
                          value: e.target.value,
                        })
                      }
                      fullWidth
                      error={!!fieldState.error}
                      placeholder="Select size"
                    >
                      {containerSizes.map((size) => (
                        <MenuItem key={size.value} value={size.value}>
                          {size.label}
                        </MenuItem>
                      ))}
                    </SelectInput>
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </Guide>
                )}
              />
            </FormControl>

            <FormControl>
              <FormLabel className="text-sm font-medium text-gray-700 mb-2">
                {t("ContainersStep.noOfContainers")}
              </FormLabel>
              <Controller
                name={`containers.${index}.count`}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <TextInput
                      {...field}
                      onChange={(e) =>
                        setQuoteRequestActionByPath({
                          path: `load_spec.containers.${index}.count`,
                          value: Number(e.target.value),
                        })
                      }
                      type="number"
                      fullWidth
                      error={!!fieldState.error}
                      placeholder="1"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </FormControl>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormControl>
              <FormLabel className="text-sm font-medium text-gray-700 mb-2">{t("ContainersStep.weight")}</FormLabel>
              <Controller
                name={`containers.${index}.weight`}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <TextInput
                      {...field}
                      onChange={(e) =>
                        setQuoteRequestActionByPath({
                          path: `load_spec.containers.${index}.weight`,
                          value: Number(e.target.value),
                        })
                      }
                      type="number"
                      fullWidth
                      error={!!fieldState.error}
                      placeholder="Enter weight"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </FormControl>

            <FormControl>
              <FormLabel className="text-sm font-medium text-gray-700 mb-2">{t("ContainersStep.volume")}</FormLabel>
              <Controller
                name={`containers.${index}.volume`}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <TextInput
                      {...field}
                      onChange={(e) =>
                        setQuoteRequestActionByPath({
                          path: `load_spec.containers.${index}.volume`,
                          value: Number(e.target.value),
                        })
                      }
                      type="number"
                      fullWidth
                      error={!!fieldState.error}
                      placeholder="Enter volume"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </FormControl>
          </div>

          {/* Conditional fields based on container type */}
          {containerType === "Reefer" && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <FormControl>
                  <FormLabel className="text-sm font-medium text-gray-700 mb-2">Temperature Min (°F)</FormLabel>
                  <Controller
                    name={`containers.${index}.min_temp`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <TextInput
                          {...field}
                          onChange={(e) =>
                            setQuoteRequestActionByPath({
                              path: `load_spec.containers.${index}.min_temp`,
                              value: Number(e.target.value),
                            })
                          }
                          type="number"
                          fullWidth
                          error={!!fieldState.error}
                          placeholder="Enter min temperature"
                        />
                        {fieldState.error && (
                          <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                            {fieldState.error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel className="text-sm font-medium text-gray-700 mb-2">Temperature Max (°F)</FormLabel>
                  <Controller
                    name={`containers.${index}.max_temp`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <TextInput
                          {...field}
                          onChange={(e) =>
                            setQuoteRequestActionByPath({
                              path: `load_spec.containers.${index}.max_temp`,
                              value: Number(e.target.value),
                            })
                          }
                          type="number"
                          fullWidth
                          error={!!fieldState.error}
                          placeholder="Enter max temperature"
                        />
                        {fieldState.error && (
                          <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                            {fieldState.error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              <div className="flex items-center space-x-2">
                <Controller
                  name={`containers.${index}.requires_genset`}
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={(e) =>
                        setQuoteRequestActionByPath({
                          path: `load_spec.containers.${index}.requires_genset`,
                          value: e.target.checked,
                        })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                />
                <Typography variant="body2">{t("ContainersStep.requiresGenset")}</Typography>
              </div>
            </div>
          )}

          {/* Remove button */}
          {canRemove && (
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onRemove}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                {t("ContainersStep.removeContainer")}
              </button>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
